import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import Matterport from "../Matterport/Matterport";
import useEventListener from "../../hooks/useEventListener";

function Modal({ 
    name, 
    description, 
    image, 
    onSetModalVisible, 
    url,
    order,
    hotspotHighlightOrigin
}) {
    const [matterportVisible, setMatterportVisible] = useState(false);
    const trackModalOpen = useCallback(
        () => {
            if (window.gtag) {
                window.gtag('event', `open_modal`, {
                    "event_category": "Modal",
                    "event_label": name
                });
            }
        },
        [name],
      );

    const modalContainer = document.querySelector("#map-grid-container");

    function handleClose(event) {
        event.preventDefault();
        event.stopPropagation();

        if (hotspotHighlightOrigin === "sidebar") {
            const lastPlaceVisited = document.querySelectorAll(`[data-order="${order}"]`);
            if (lastPlaceVisited.length === 1 && lastPlaceVisited[0].firstElementChild.tagName.toLowerCase() === "button") {
                lastPlaceVisited[0].firstElementChild.focus();
            };
        } else if (hotspotHighlightOrigin === "map") {
            const lastPlaceVisited = document.querySelectorAll(`[data-number="${order}"]`);
            if (lastPlaceVisited.length === 1 && lastPlaceVisited[0].tagName.toLowerCase() === "button") {
                lastPlaceVisited[0].focus();
            };
        }
        onSetModalVisible(null);
    }

    function handleBackgroundClick(e) {
        if (e.target === e.currentTarget) {
            handleClose(e);
        }
    };

    function handleMatterporOpen(event) {
        event.preventDefault();
        event.stopPropagation();

        setMatterportVisible(true);
        trackLinkOpen();
    }

    function trackLinkOpen() {
        if (window.gtag) {
            window.gtag('event', `matterport_modal`, {
                "event_category": "Matterport",
                "event_label": name
            });
        }
    }

    useEffect(() => {
        trackModalOpen();

        document.querySelector(".close-pop-up").focus();
    }, [trackModalOpen])

    function handleKeyDown (event) {
        if (event.keyCode === 27 && !matterportVisible) {
            handleClose(event);
        };
    };

    useEventListener("keydown", handleKeyDown);

    return createPortal(
        <>
            <div
                className="pop-up-container"
                onClick={handleBackgroundClick}
            >
                <div 
                    className="pop-up"
                    role="dialog"
                    aria-labelledby="dialogTitle"
                    aria-describedby="dialogDesc"
                >
                    <div className="pop-up-heading">
                        <div className="pop-up-text">
                            <h2 id="dialogTitle">{name}</h2>
                            <p id="dialogDesc">{description}</p>
                        </div>
                        <button 
                            className="close-pop-up" 
                            onClick={event => handleClose(event)}
                            onKeyDown={event => event.keyCode === 13 ? handleClose(event) : null}
                            aria-label="Close modal"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="26" viewBox="0 0 28 26">
                                <path fill="none" stroke="#888888" strokeWidth="3" d="M3.2,2l21.6,21.6"/>
                                <path fill="none" stroke="#888888" strokeWidth="3" d="M25,2L3,24"/>
                            </svg>
                        </button>
                    </div>

                    <div className="pop-up-image">
                        <img src={`${process.env.PUBLIC_URL}/assets/${image}`} alt="" />
                    </div>

                    {url && <button
                        className="review-button"
                        onClick={(event) => handleMatterporOpen(event)}
                    >
                        Explore the Space
                    </button>}
                </div>
            </div>
            {matterportVisible && (
                <Matterport 
                    source={url} onSetMatterportVisible={setMatterportVisible} 
                    opener={document.querySelector(".review-button")} 
                />
            )}
        </>,
        modalContainer
    );
}

Modal.propTypes = {
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    onSetModalVisible: PropTypes.func.isRequired,
    hotspotHighlightOrigin: PropTypes.string
};

export default Modal;
